import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { caseStudies } from '../data/caseStudiesData';
import { ArrowLeft, Building, Calendar, DollarSign, Users, Wrench, Target, CheckCircle } from 'lucide-react';

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const caseStudy = caseStudies.find(cs => cs.slug === slug);

  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  const { title, category, details, image, client, duration, value } = caseStudy;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Image */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/case-studies" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#005B63] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Project Showcase</span>
          </Link>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-4">
            {category.map((cat, index) => (
              <span 
                key={index}
                className="px-3 py-1 bg-[#005B63]/10 text-[#005B63] text-sm font-medium rounded-full"
              >
                {cat}
              </span>
            ))}
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-6 mb-8 text-gray-600">
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5 text-[#005B63]" />
              <span>{client}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#005B63]" />
              <span>{duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#005B63]" />
              <span>{value}</span>
            </div>
          </div>
          
          {/* Hero Image */}
          {image && (
            <div className="relative h-64 sm:h-80 lg:h-[400px] overflow-hidden rounded-2xl shadow-lg">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Overview */}
              {details?.overview && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <Target className="w-6 h-6 text-[#005B63]" />
                    Overview
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {details.overview}
                  </p>
                </div>
              )}

              {/* Challenge */}
              {details?.challenge && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <span className="w-6 h-6 bg-[#005B63] text-white rounded-full flex items-center justify-center text-sm font-bold">!</span>
                    The Challenge
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {details.challenge}
                  </p>
                </div>
              )}

              {/* Solution */}
              {details?.solution && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <Wrench className="w-6 h-6 text-[#005B63]" />
                    Our Solution
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {details.solution}
                  </p>
                </div>
              )}

              {/* Outcome */}
              {details?.outcome && (
                <div className="bg-[#005B63]/5 border border-[#005B63]/20 rounded-2xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-[#005B63]" />
                    Results & Impact
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {details.outcome}
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                {/* Project Details Card */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Project Details</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Building className="w-5 h-5 text-[#005B63] mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Client</p>
                        <p className="font-medium text-gray-900">{client}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-[#005B63] mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Duration</p>
                        <p className="font-medium text-gray-900">{duration}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <DollarSign className="w-5 h-5 text-[#005B63] mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Project Value</p>
                        <p className="font-medium text-gray-900">{value}</p>
                      </div>
                    </div>

                    {details?.team && (
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-[#005B63] mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Team Size</p>
                          <p className="font-medium text-gray-900">{details.team}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Technologies */}
                {details?.technologies && details.technologies.length > 0 && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Technologies Used</h3>
                    <div className="flex flex-wrap gap-2">
                      {details.technologies.map((tech, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-[#005B63] rounded-2xl p-6 text-center">
                  <h3 className="text-lg font-bold text-white mb-2">Ready to Start?</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Let's discuss how we can support your project.
                  </p>
                  <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer" className="block w-full bg-white text-[#005B63] py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                    Schedule a Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Case Studies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Projects</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {caseStudies
              .filter(cs => cs.slug !== slug)
              .slice(0, 3)
              .map((study) => (
                <Link
                  key={study.id}
                  to={`/case-studies/${study.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 group-hover:text-[#005B63] transition-colors line-clamp-2">
                      {study.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2">{study.client}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyDetail;
